import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheckIcon as Shield,
  MagnifyingGlassIcon as Search,
  FunnelIcon as Filter,
  EyeIcon as Eye,
  EyeSlashIcon as EyeOff,
  ExclamationTriangleIcon as AlertTriangle,
  CircleStackIcon as Database,
  CalendarIcon as Calendar,
  EnvelopeIcon as Mail,
  UserIcon as User,
  TrashIcon as Trash2,
  ArrowPathIcon as RefreshCw,
  TicketIcon as Ticket,
} from '@heroicons/react/24/outline';
import {
  leakedCredentialsService,
  LeakedCredential,
  CredentialStats,
  ListCredentialsParams,
  // PaginatedLeakedCredentials
} from '../services/leakedCredentials';
import { TicketPriority, TicketCategory, CreateTicketRequest } from '../types/ticket';
import { ticketService } from '../services/tickets';
import toast from 'react-hot-toast';
import { companiesService, Company } from '../services/companies';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../services/users';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/i18nFormat';

const LeakedCredentials: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [credentials, setCredentials] = useState<LeakedCredential[]>([]);
  const [paginationInfo, setPaginationInfo] = useState({
    total: 0,
    page: 1,
    per_page: 50,
    total_pages: 0,
  });
  const [stats, setStats] = useState<CredentialStats | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showPasswords, setShowPasswords] = useState<Record<number, boolean>>({});

  // Verificar se o usuário é CLIENT
  const isClientUser = user?.role === UserRole.CLIENT;

  // Filtros
  const [filters, setFilters] = useState<ListCredentialsParams>({
    limit: 50,
    offset: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<number | ''>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');
  const [selectedDatabase, setSelectedDatabase] = useState<string>('');

  // Paginação
  // const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const createTicketFromCredential = async (credential: LeakedCredential) => {
    try {
      const subject = credential.email || credential.username || credential.domain;
      const title = `Leaked credential: ${subject}`;
      const details = [
        credential.email ? `**Email**: ${credential.email}` : null,
        credential.username ? `**Username**: ${credential.username}` : null,
        credential.domain ? `**Domain**: ${credential.domain}` : null,
        credential.database_name ? `**Database**: ${credential.database_name}` : null,
        (() => {
          const d = getBreachDate(credential);
          return d ? `**Breach date**: ${d}` : null;
        })(),
        credential.severity ? `**Severity**: ${credential.severity}` : null,
        credential.source ? `**Source**: ${credential.source}` : null,
        credential.source_reference ? `**Ref**: ${credential.source_reference}` : null,
      ].filter(Boolean).join('\n\n');

      const sev = (credential.severity || 'low').toLowerCase();
      const priorityMap: Record<string, TicketPriority> = {
        critical: TicketPriority.CRITICAL,
        high: TicketPriority.HIGH,
        medium: TicketPriority.MEDIUM,
        low: TicketPriority.LOW,
      };
      const payload: CreateTicketRequest = {
        title,
        description: details || title,
        priority: priorityMap[sev] || TicketPriority.LOW,
        category: TicketCategory.SECURITY_INCIDENT,
      };

      await ticketService.createTicket(payload);
      toast.success(t('leakedCredentials.toasts.ticketCreated'));
    } catch (error) {
      console.error('Error creating ticket from credential', error);
      toast.error(t('leakedCredentials.errors.createTicket'));
    }
  };

  const loadCredentials = useCallback(async () => {
    try {
      const response = await leakedCredentialsService.listCredentials(filters);
      setCredentials(response.data);
      setPaginationInfo({
        total: response.total,
        page: response.page,
        per_page: response.per_page,
        total_pages: response.total_pages,
      });
    } catch (error) {
      console.error(t('leakedCredentials.errors.loadCredentials'), error);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    try {
      const statsParams = selectedCompany ? { company_id: Number(selectedCompany) } : {};
      const data = await leakedCredentialsService.getStats(statsParams);
      setStats(data);
    } catch (error) {
      console.error(t('leakedCredentials.errors.loadStats'), error);
    }
  }, [selectedCompany]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCredentials(),
        loadStats()
      ]);
    } catch (error) {
      console.error(t('leakedCredentials.errors.loadData'), error);
    } finally {
      setLoading(false);
    }
  }, [loadCredentials, loadStats]);

  const loadCompanies = useCallback(async () => {
    try {
      // Usuários CLIENT não precisam carregar lista de empresas
      if (isClientUser) {
        if (user?.company_id) {
          setSelectedCompany(user.company_id);
        }
        return;
      }

      const data = await companiesService.getAll();
      setCompanies(data);
    } catch (error) {
      console.error(t('leakedCredentials.errors.loadCompanies'), error);
    }
  }, [isClientUser, user]);

  useEffect(() => {
    loadData();
    loadCompanies();
  }, [loadData, loadCompanies]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleSearch = () => {
    const newFilters: ListCredentialsParams = {
      ...filters,
      offset: 0,
      company_id: selectedCompany ? Number(selectedCompany) : undefined,
      severity: selectedSeverity || undefined,
      database_name: selectedDatabase || undefined,
      subdomain: searchTerm || undefined,
    };

    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCompany('');
    setSelectedSeverity('');
    setSelectedDatabase('');
    setFilters({ limit: itemsPerPage, offset: 0 });
  };

  const togglePasswordVisibility = (credentialId: number) => {
    setShowPasswords(prev => ({
      ...prev,
      [credentialId]: !prev[credentialId],
    }));
  };

  const getBreachDate = (c: LeakedCredential): string | undefined => {
    const bd = c.breach_date || (c as any).breachDate;
    const dd = c.discovered_date || (c as any).discoveredDate;
    return bd || dd || c.created_at || (c as any).createdAt || (c as any).date;
  };

  const handleStatusChange = async (credential: LeakedCredential, newStatus: string) => {
    try {
      const updated = await leakedCredentialsService.updateCredentialStatus(
        credential.id,
        newStatus,
        credential.domain
      );
      setCredentials(prev => prev.map(c => c.id === credential.id ? { ...c, status: updated.status } : c));
      toast.success(t('leakedCredentials.toasts.statusUpdated'));
    } catch (error) {
      console.error(t('leakedCredentials.errors.updateStatus'), error);
      toast.error(t('leakedCredentials.errors.updateStatus'));
    }
  };

  const handleDeleteCredential = async (credentialId: number) => {
    if (!window.confirm(t('leakedCredentials.actions.confirmDelete'))) {
      return;
    }

    try {
      await leakedCredentialsService.deleteCredential(credentialId);
      await loadCredentials();
    } catch (error) {
      console.error(t('leakedCredentials.errors.deleteCredential'), error);
      alert(t('leakedCredentials.errors.deleteCredential'));
    }
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * itemsPerPage,
    }));
  };

  const getUniqueValues = (field: keyof LeakedCredential): string[] => {
    const values = credentials
      .map(cred => cred[field] as string)
      .filter(Boolean)
      .filter((value, index, array) => array.indexOf(value) === index);
    return values.sort();
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
        <span className='ml-2'>{t('leakedCredentials.loading')}</span>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center'>
            <Shield className='mr-2 h-6 w-6 text-red-600' />
            {t('leakedCredentials.title')}
          </h1>
          <p className='text-gray-600 mt-1'>
            {t('leakedCredentials.subtitle')}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className='flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50'
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </button>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
          <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow border dark:border-gray-700'>
            <div className='flex items-center'>
              <Database className='h-8 w-8 text-blue-600' />
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>{t('leakedCredentials.stats.totalCredentials')}</p>
                <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                  {stats.total_credentials.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow border dark:border-gray-700'>
            <div className='flex items-center'>
              <Mail className='h-8 w-8 text-green-600' />
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>{t('leakedCredentials.stats.uniqueEmails')}</p>
                <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                  {stats.unique_emails.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow border dark:border-gray-700'>
            <div className='flex items-center'>
              <Database className='h-8 w-8 text-purple-600' />
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>{t('leakedCredentials.stats.databases')}</p>
                <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                  {stats.unique_databases.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow border dark:border-gray-700'>
            <div className='flex items-center'>
              <AlertTriangle className='h-8 w-8 text-red-600' />
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>{t('leakedCredentials.stats.recentDiscoveries')}</p>
                <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                  {stats.recent_discoveries.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className='bg-white dark:bg-gray-800 p-6 rounded-lg shadow border dark:border-gray-700'>
        <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>{t('leakedCredentials.filters.searchByDomain')}</label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
              <input
                type='text'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('leakedCredentials.filters.domainPlaceholder')}
                className='pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
            </div>
          </div>

          {/* Seletor de empresa - apenas para usuários não CLIENT */}
          {!isClientUser && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                {t('common.company')}
              </label>
              <div className='relative'>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value ? Number(e.target.value) : '')}
                  className='appearance-none w-full px-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  <option value=''>{t('leakedCredentials.filters.allCompanies')}</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                <svg
                  className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
                </svg>
              </div>
            </div>
          )}

          {/* Informação da empresa para usuários CLIENT */}
          {isClientUser && user?.company && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>{t('common.company')}</label>
              <div className='w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md'>
                <span className='text-gray-900 dark:text-gray-300'>{user.company.name}</span>
                <p className='text-sm text-blue-600 mt-1'>{t('leakedCredentials.filters.showingCompanyCredentials')}</p>
              </div>
            </div>
          )}

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {t('leakedCredentials.filters.severity')}
            </label>
            <div className='relative'>
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className='appearance-none w-full px-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              >
                <option value=''>{t('leakedCredentials.filters.allSeverities')}</option>
                <option value='low'>{t('common.severity.low')}</option>
                <option value='medium'>{t('common.severity.medium')}</option>
                <option value='high'>{t('common.severity.high')}</option>
                <option value='critical'>{t('common.severity.critical')}</option>
              </select>
              <svg
                className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
              </svg>
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {t('leakedCredentials.filters.database')}
            </label>
            <div className='relative'>
              <select
                value={selectedDatabase}
                onChange={(e) => setSelectedDatabase(e.target.value)}
                className='appearance-none w-full px-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              >
                <option value=''>{t('leakedCredentials.filters.allDatabases')}</option>
                {stats?.by_database ? (
                  Object.keys(stats.by_database).sort().map(db => (
                    <option key={db} value={db}>
                      {db} ({stats.by_database[db]})
                    </option>
                  ))
                ) : (
                  getUniqueValues('database_name').map(db => (
                    <option key={db} value={db}>
                      {db}
                    </option>
                  ))
                )}
              </select>
              <svg
                className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
              </svg>
            </div>
          </div>

          <div className='flex items-end space-x-2'>
            <button
              onClick={handleSearch}
              className='flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'
            >
              <Filter className='mr-2 h-4 w-4' />
              {t('common.filter')}
            </button>
            <button
              onClick={handleClearFilters}
              className='px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400'
            >
              {t('common.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de Credenciais */}
      <div className='bg-white dark:bg-gray-800 rounded-xl shadow border dark:border-gray-700 overflow-visible'>
        <div className='overflow-x-hidden'>
          <table className='w-full table-auto divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-2 w-3/12 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('leakedCredentials.table.credential')}
                </th>
                <th className='px-4 py-2 w-3/12 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('leakedCredentials.table.password')}
                </th>
                <th className='px-4 py-2 w-2/12 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('leakedCredentials.table.database')}
                </th>
                <th className='px-4 py-2 w-1/12 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('leakedCredentials.table.breachDate')}
                </th>
                <th className='px-4 py-2 w-1/12 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('leakedCredentials.table.severity')}
                </th>
                <th className="px-4 py-2 w-1/12 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('leakedCredentials.table.status')}
                </th>
                <th className='px-4 py-2 w-1/12 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
              {credentials.map(credential => (
                <tr key={credential.id} className='hover:bg-gray-50'>
                  <td className='px-4 py-3 align-top whitespace-normal break-words'>
                    <div className='flex items-start'>
                      {credential.email ? (
                        <Mail className='h-4 w-4 text-gray-400 mr-2' />
                      ) : (
                        <User className='h-4 w-4 text-gray-400 mr-2' />
                      )}
                      <div>
                        <div className='text-sm font-medium text-gray-900 dark:text-white break-all'>
                          {credential.email || credential.username || t('common.notAvailable')}
                        </div>
                        <div className='text-sm text-gray-500 break-all'>{credential.domain}</div>
                      </div>
                    </div>
                  </td>
                  <td className='px-4 py-3 align-top whitespace-normal break-words'>
                    <div className='flex items-start'>
                      <span className='text-sm text-gray-900 dark:text-gray-300 mr-2 break-all'>
                        {showPasswords[credential.id]
                          ? (credential.password || credential.hashed_password || t('common.notAvailable'))
                          : leakedCredentialsService.maskPassword(credential.password || credential.hashed_password)
                        }
                      </span>
                      <button
                        onClick={() => togglePasswordVisibility(credential.id)}
                        className='text-gray-400 hover:text-gray-600'
                      >
                        {showPasswords[credential.id] ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                      </button>
                    </div>
                    {credential.hash_type && (
                      <div className='text-xs text-gray-500'>
                        {t('common.hash')}: {credential.hash_type}
                      </div>
                    )}
                  </td>
                  <td className='px-4 py-3 align-top whitespace-normal break-words'>
                    <div className='flex items-start'>
                      <Database className='h-4 w-4 text-gray-400 mr-2' />
                      <span className='text-sm text-gray-900 dark:text-gray-300 break-all'>
                        {credential.database_name || t('common.notAvailable')}
                      </span>
                    </div>
                  </td>
                  <td className='px-4 py-3 align-top whitespace-normal break-words'>
                    <div className='flex items-start'>
                      <Calendar className='h-4 w-4 text-gray-400 mr-2' />
                      <span className='text-sm text-gray-900 dark:text-gray-300'>
                        {getBreachDate(credential) ? formatDate(getBreachDate(credential) as string) : t('common.notAvailable')}
                      </span>
                    </div>
                  </td>
                  <td className='px-4 py-3 align-top whitespace-normal break-words'>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${leakedCredentialsService.getSeverityBadgeColor(credential.severity)}`}>
                      {t(`common.severity.${credential.severity || 'low'}`)}
                    </span>
                  </td>
                  <td className='px-4 py-3 align-top whitespace-normal break-words'>
                    <select
                      className="text-sm border rounded-md px-2 py-1 bg-white dark:bg-gray-700 dark:text-gray-200"
                      value={credential.status || 'new'}
                      onChange={(e) => handleStatusChange(credential, e.target.value)}
                    >
                      <option value="new">{t('leakedCredentials.status.new')}</option>
                      <option value="in_progress">{t('leakedCredentials.status.in_progress')}</option>
                      <option value="resolved">{t('leakedCredentials.status.resolved')}</option>
                      <option value="false_positive">{t('leakedCredentials.status.false_positive')}</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 align-top whitespace-normal text-sm font-medium">
                    <button
                      onClick={() => createTicketFromCredential(credential)}
                      className="inline-flex items-center p-2 text-indigo-600 hover:text-indigo-900 mr-3"
                      aria-label={t('leakedCredentials.actions.createTicket')}
                      title={t('leakedCredentials.actions.createTicket')}
                    >
                      <Ticket className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteCredential(credential.id)}
                      className='text-red-600 hover:text-red-900'
                      title={t('leakedCredentials.actions.deleteTooltip')}
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {credentials.length === 0 && (
          <div className='text-center py-12'>
            <Shield className='mx-auto h-12 w-12 text-gray-400' />
            <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-gray-300'>{t('leakedCredentials.empty.title')}</h3>
            <p className='mt-1 text-sm text-gray-500'>{t('leakedCredentials.empty.subtitle')}</p>
          </div>
        )}
      </div>

      {/* Paginação */}
      {paginationInfo.total > 0 && (
        <div className='flex items-center justify-between'>
          <div className='text-sm text-gray-700'>
            {t('common.pagination.showing', {
              from: ((paginationInfo.page - 1) * paginationInfo.per_page) + 1,
              to: Math.min(paginationInfo.page * paginationInfo.per_page, paginationInfo.total),
              total: paginationInfo.total
            })}
          </div>
          <div className='flex items-center space-x-2'>
            <span className='text-sm text-gray-500'>
              {t('common.pagination.pageOf', { page: paginationInfo.page, total: paginationInfo.total_pages })}
            </span>
            <button
              onClick={() => handlePageChange(paginationInfo.page - 1)}
              disabled={paginationInfo.page === 1}
              className='px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {t('common.pagination.prev')}
            </button>
            <button
              onClick={() => handlePageChange(paginationInfo.page + 1)}
              disabled={paginationInfo.page >= paginationInfo.total_pages}
              className='px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {t('common.pagination.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeakedCredentials;
